import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { fetchStock } from '../adminApi'
import type { StockItem, StockItemKind, StockLocation } from '../../core/types/stock'

export type StockFilter = 'alles' | 'op' | StockItemKind

/**
 * De voorraad van de server, gedeeld door de tabbladen Overzicht, Tellen en Inkoop. Na een actie
 * zet het scherm de teruggegeven artikelen in de lijst (de server rekende de stand), zonder alles
 * opnieuw te laden.
 */
export function useStock() {
  const { data, status, reload } = useLatestLoad(fetchStock)
  const [items, setItems] = useState<StockItem[] | null>(null)
  const [lastCountAt, setLastCountAt] = useState<Record<StockLocation, string | null>>({ webshop: null, salon: null })

  useEffect(() => {
    if (!data) return
    setItems(data.items)
    setLastCountAt(data.lastCountAt)
  }, [data])

  const replaceMany = useCallback((next: readonly StockItem[]) => {
    if (next.length === 0) return
    const byId = new Map(next.map((i) => [i.id, i]))
    setItems((all) => {
      const list = all ?? []
      const known = new Set(list.map((i) => i.id))
      return [...list.map((i) => byId.get(i.id) ?? i), ...next.filter((i) => !known.has(i.id))]
    })
  }, [])
  const replace = useCallback((item: StockItem) => replaceMany([item]), [replaceMany])

  /** Na een volledige telling: de datum bijwerken zonder te herladen. */
  const markCounted = useCallback((location: StockLocation, at: string) => setLastCountAt((c) => ({ ...c, [location]: at })), [])

  return { items, status, reload, replace, replaceMany, lastCountAt, markCounted }
}

export type StockControl = ReturnType<typeof useStock>

/** Zoeken en filteren in de lijst; de startfilter komt uit de link (bijv. vanaf het overzicht: ?filter=op). */
export function useStockFilter(items: readonly StockItem[] | null) {
  const [params] = useSearchParams()
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<StockFilter>(() => {
    const asked = params.get('filter')
    return asked === 'op' || asked === 'retail' || asked === 'verbruik' ? asked : 'alles'
  })

  const term = query.trim().toLowerCase()
  const visible = (items ?? []).filter((i) => {
    if (term && !`${i.name} ${i.brand ?? ''}`.toLowerCase().includes(term)) return false
    if (filter === 'op') return Object.values(i.levels).some((v) => v !== null && v <= 0)
    if (filter === 'retail' || filter === 'verbruik') return i.kind === filter
    return true
  })

  return { visible, query, setQuery, filter, setFilter }
}
